import React, { useEffect } from 'react';
import { PageHeader } from '../components/page/PageHeader';
import { FeatureRow } from '../components/page/FeatureRow';
import { CtaBand } from '../components/page/CtaBand';
import { PlayCircle } from 'lucide-react';
import { Counter } from '../components/ui/Counter';
import { Reveal } from '../components/ui/Reveal';
import { LayeredPartnerRosterMockup, LayeredAListWedgeMockup, LayeredAgencyOutreachMockup } from '../components/page/MarketingMockups';
import { HowMarketplaceWorks } from '../components/page/HowMarketplaceWorks';
import { Testimonials, BRAND_TESTIMONIALS } from '../components/Testimonials';
const DEMO_VIDEO_URL = 'https://www.youtube.com/watch?v=U2sV81k41u4&pp=0gcJCUwLAYcqIYzv';
const SEO = {
  title: 'Partner With Talent Agencies More Efficiently | Zano for Marketing Agencies',
  description: 'Search live talent agency rosters, discover represented creators, collaborate with talent agencies at scale, and source creators more efficiently across every client campaign.',
  keywords: 'marketing agency software, influencer sourcing, talent agency directory, live talent agency rosters, represented creators, influencer workflow, partner with talent agencies, creator sourcing',
  ogTitle: 'For Marketing Agencies | Source Represented Creators through Talent Agencies with Zano',
  ogDescription: 'Access live talent agency rosters, discover represented creators, and streamline influencer sourcing with real-time performance insights.'
};
function updateMetaTag(attribute: 'name' | 'property', key: string, content: string) {
  const selector = `meta[${attribute}="${key}"]`;
  const existingTag = document.head.querySelector<HTMLMetaElement>(selector);
  const tag = existingTag ?? document.createElement('meta');
  const previousContent = existingTag?.getAttribute('content') ?? null;
  if (!existingTag) {
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
  return () => {
    if (!existingTag) {
      tag.remove();
      return;
    }
    if (previousContent === null) {
      existingTag.removeAttribute('content');
    } else {
      existingTag.setAttribute('content', previousContent);
    }
  };
}
export const ForMarketingAgencies = () => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = SEO.title;
    const restoreDescription = updateMetaTag('name', 'description', SEO.description);
    const restoreKeywords = updateMetaTag('name', 'keywords', SEO.keywords);
    const restoreOgTitle = updateMetaTag('property', 'og:title', SEO.ogTitle);
    const restoreOgDescription = updateMetaTag('property', 'og:description', SEO.ogDescription);
    return () => {
      document.title = previousTitle;
      restoreDescription();
      restoreKeywords();
      restoreOgTitle();
      restoreOgDescription();
    };
  }, []);
  return <>
      <PageHeader eyebrow="For Marketing Agencies" eyebrowClass="bg-purple-50 text-purple-700 border border-purple-200" title="Keep your talent agency partners front and center" subtitle="Zano makes it easy and quick to keep the talent agency rosters you already partner with in front of you for quick reference when building campaigns. Plus, always keep in touch with the newest talent agencies." primaryCta={{
      label: 'Start Free Trial',
      to: '/pricing'
    }} secondaryCta={{
      label: 'Watch demo video',
      href: DEMO_VIDEO_URL,
      newTab: true,
      icon: <PlayCircle size={18} className="mr-1.5" data-id="element-2577" data-mp="12yqf945o4wu3" />
    }} data-id="element-2576" data-mp="7r7msgrc2ohl" />

      {/* Metrics strip */}
      <section className="border-y border-gray-100 bg-gray-50/60 py-10" data-id="element-2578" data-mp="17rjljwgscd8a">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-8 text-center" data-id="element-2579" data-mp="1cqe2esv6zrpr">
          {[{
          v: <Counter to={400} suffix="+" data-id="element-2580" data-mp="1gtj3qs3jdnct" />,
          l: 'Talent agencies'
        }, {
          v: <Counter to={6500} suffix="+" data-id="element-2581" data-mp="1loq2rmlv23ve" />,
          l: 'Represented creators'
        }, {
          v: <Counter to={100} suffix="" data-id="element-2582" data-mp="142gvhau1yn8k" />,
          l: 'Avg submissions / 24h'
        }, {
          v: <Counter to={500} prefix="$" suffix="k+" data-id="element-2583" data-mp="1huexz2t9dkvr" />,
          l: 'GMV generated'
        }].map((s, i) => <Reveal key={i} delay={i * 0.08} data-id="element-2584" data-mp="1cvc1i6rcgmze" data-mpi={i}>
              <p className="text-3xl font-bold text-zano-pink" data-id="element-2585" data-mp="p4y5y69jw602">{s.v}</p>
              <p className="text-sm text-gray-600 mt-1" data-id="element-2586" data-mp="p7alkcru649y">{s.l}</p>
            </Reveal>)}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 divide-y divide-gray-100" data-id="element-2587" data-mp="z346okjmf63c">
        <FeatureRow eyebrow="Partner Management" title="Keep partner rosters accessible" body="Already work with top talent agencies? Keep track of their live rosters on Zano instead of asking for updated PDFs every month. Quickly reference them when building campaigns for clients so you can move fast." bullets={['Filter the database by specific talent agencies', 'Always see their most up-to-date roster', 'Move faster on quick-turnaround client campaigns']} visual={<LayeredPartnerRosterMockup data-id="element-2589" data-mp="3ehz8z5r4c2bd" />} data-id="element-2588" data-mp="u4armnoor712" />
        <FeatureRow eyebrow="The Wedge" title="A-list access for your brand clients" body="Access to A-list talent gives your agency a powerful wedge for your brand clients. Suddenly, you know exactly how to get in touch with the highest level of creators and have direct contact with their talent managers." bullets={['Direct access to talent managers for top creators', 'Impress clients with premium talent options', 'No more guessing who represents who']} visual={<LayeredAListWedgeMockup data-id="element-2591" data-mp="tx8tdx8v8kr5" />} flip data-id="element-2590" data-mp="3jf7rx8k3cado" />
        <FeatureRow eyebrow="Direct Outreach" title="Partner directly on deals" body="The talent agencies on Zano are here to get more brand deals for their talent, so they are happy to partner with you for your brand clients. Approach them directly by email or post a casting call to get custom submissions." bullets={['Direct email contact info for talent managers', 'Post casting calls to receive custom quotes', 'Build long-term relationships with top agencies']} visual={<LayeredAgencyOutreachMockup data-id="element-2593" data-mp="kvoa7617aqam" />} data-id="element-2592" data-mp="ypiemzz1cwn3" />
      </div>

      <HowMarketplaceWorks audience="you" data-id="element-2594" data-mp="ku3gtjqmhdec" />

      <Testimonials title="What marketing agencies are saying" items={BRAND_TESTIMONIALS} data-id="element-2595" data-mp="fwboiom2fp9m" />

      <CtaBand title="Build client campaigns faster" subtitle="Join the marketing agencies using Zano to keep their talent partners close." primaryCta={{
      label: 'Start Free Trial',
      to: '/pricing'
    }} secondaryCta={{
      label: 'Watch demo video',
      href: DEMO_VIDEO_URL,
      newTab: true,
      icon: <PlayCircle size={18} className="mr-1.5" data-id="element-2597" data-mp="2hlqx838cp5yo" />
    }} data-id="element-2596" data-mp="ay0cc78at2o8" />
    </>;
};